import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";
import type { TemplateFieldDef } from "../services/buildEmptySurveyData";

// Use untyped alias since attribute_templates isn't in generated types
const db = supabase as unknown as any;

interface TemplatePreviewDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function countLeaves(fields: TemplateFieldDef[]): number {
  return fields.reduce(
    (count, field) =>
      field.type === "node" && Array.isArray(field.fields)
        ? count + countLeaves(field.fields)
        : count + 1,
    0
  );
}

function TemplateFieldRow({ field, depth }: { field: TemplateFieldDef; depth: number }) {
  if (field.type === "node" && Array.isArray(field.fields)) {
    return (
      <div className={cn(depth > 0 && "pl-4")}>
        <div className="flex items-center gap-2 py-2">
          <span className={cn("font-semibold text-foreground", depth === 0 ? "text-sm" : "text-xs uppercase tracking-wide text-muted-foreground")}>
            {field.title}
          </span>
          <span className="text-xs text-muted-foreground/60">· {countLeaves(field.fields)} fields</span>
        </div>
        <div className="space-y-1 border-l border-border/40 pl-3">
          {field.fields.map((child) => (
            <TemplateFieldRow key={child.id} field={child} depth={depth + 1} />
          ))}
        </div>
      </div>
    );
  }

  const options = Array.isArray(field.options) ? (field.options as string[]) : null;
  const hint = typeof field.hint === "string" && field.hint.trim() !== "" ? field.hint : null;

  return (
    <div className="py-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground">
          {field.title}
          {field.required === true && <span className="ml-0.5 text-destructive">*</span>}
        </span>
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
          {field.type}
        </Badge>
      </div>
      {hint && <p className="mt-0.5 text-xs italic text-muted-foreground/70">{hint}</p>}
      {options && options.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {options.map((option) => (
            <span
              key={option}
              className="rounded-full border border-border/50 bg-muted/30 px-1.5 py-0.5 text-[10px] text-muted-foreground"
            >
              {option}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function TemplatePreviewDialog({ projectId, open, onOpenChange }: TemplatePreviewDialogProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["project-attribute-template-preview", projectId],
    queryFn: async (): Promise<{ fields: TemplateFieldDef[]; version: string | null }> => {
      const { data: template, error: templateError } = await db
        .from("attribute_templates")
        .select("schema_definition, version")
        .eq("organization_id", projectId)
        .order("version", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (templateError) throw templateError;
      const fields: TemplateFieldDef[] = !template?.schema_definition
        ? []
        : typeof template.schema_definition === "string"
          ? JSON.parse(template.schema_definition)
          : template.schema_definition;
      return { fields, version: template?.version ?? null };
    },
    enabled: open && !!projectId,
    staleTime: 5 * 60 * 1000,
  });

  const fields = data?.fields ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Survey template preview</DialogTitle>
          <DialogDescription>
            {data?.version ? `Version ${data.version} · ` : ""}
            New surveys start with this structure and empty answers.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 rounded-md" />
            ))}
          </div>
        ) : error ? (
          <p className="py-6 text-center text-sm text-destructive">
            Failed to load the template. Please try again.
          </p>
        ) : fields.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No survey template configured for this project.
          </p>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-3">
            <div className="space-y-2">
              {fields.map((field) => (
                <TemplateFieldRow key={field.id} field={field} depth={0} />
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
